<template>
  <div
    ref="elementRef"
    class="w-full overflow-hidden"
    :class="[
      roundedCornersClasses,
    ]"
  >
    <div
      class="relative grid"
    >
      <div
        class="absolute-0 z-0 flex grid-area-stack"
      >
        <base-picture
          v-bind="responsiveMedia.images"
          class="w-full"
          :fetchpriority
          fit="cover"
          :lazy
          :position="objectPosition"
        />
        <cms-base-gradient
          v-if="gradientStyle && gradientStyle !== 'off' && !isTextAreaOnlyGradient"
          v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
          position="bottom"
        />
      </div>
      <div
        v-style:bg="backdrop"
        class="relative z-1 w-full f-col gap-6 overflow-hidden px-4 py-6 md:p-8"
        :class="[alignmentClasses[alignment], textAlignmentClasses[alignment], { 'backdrop-blur-xs': overlayType === 'blur' }]"
      >
        <cms-base-gradient
          v-if="isTextAreaOnlyGradient"
          v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
          position="bottom"
        />
        <div v-if="eyebrowLogoResponsive || overline || title || richText" class="f-col gap-4" :class="textContainerClasses">
          <cms-base-eyebrow
            v-if="eyebrowLogoResponsive"
            :eyebrow-logo-responsive
            :eyebrow-logo-width-responsive
            :position="alignment"
          />
          <p
            v-if="overline"
            v-style:c="overlineColor"
            class="eyebrow o:overline-1"
            data-test-id="cms-thin-banner-overline"
          >
            {{ overline }}
          </p>
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-thin-banner-title"
          >
            <cms-plain-text :content field-name="title" />
          </component>
          <cms-rich-text
            v-if="richText"
            v-style:c="richTextColor"
            class="pointer-within"
            :content="{ richText }"
          />
        </div>
        <cms-shared-cta-group
          v-if="linkType !== 'No-CTA' && targets.length"
          button-class="min-w-23.75"
          class="<md:w-full"
          :class="ctaAlignmentClasses[alignment]"
          :cta-type
          :layout="ctaClasses"
          :multilink-style
          :multilink-title
          :targets
          :text-link-style="getTextLinkStyles(content)"
          @click="handlePromotionClick(`cta${$event + 1}`)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type {
  ThinBannerContent
} from '#types/components/cms/thin-banner'

import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const { content, firstElement, index, isPartOfCollection, parentHeadingLevel } = defineProps<{
  content: ThinBannerContent
  firstElement?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
}>()

const {
  alignment,
  anchor,
  backdrop,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  hideTitle,
  inlineCTAs,
  linkType,
  media,
  multilinkStyle,
  multilinkTitle,
  name,
  gradientBreakpoints,
  gradientGrid,
  gradientStyle,
  gradientTone,
  overlayType,
  overline,
  overlineColor,
  promotionTracking,
  richText,
  richTextColor,
  targets = [],
  title,
  titleColor,
  titleStyle,
  titleH1,
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const appConfig = useAppConfig()
const cms = appConfig.components.cms
const { componentCorners } = cms.section
const { enableTextAreaOnlyGradient } = cms.gradient
const isTextAreaOnlyGradient = isTextAreaOnlyGradientEnabled(enableTextAreaOnlyGradient, gradientStyle)

const objectPosition = toObjectPosition(anchor)
const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)

const responsiveMedia = getResponsiveMedia(media)
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const alignmentClasses = {
  left: 'between items-start md:flex-row md:items-center gap-8',
  center: 'justify-center items-center md:flex-col gap-6',
  right: 'between items-end md:flex-row-reverse md:items-center gap-8'
}

const ctaClasses = computed(() => {
  const smClass = targets.length === 4
    ? '<md:grid <md:cols-2 <md:auto-rows-fr <md:w-full'
    : getValueForBreakpoint('sm', equalTargetSize)
      ? '<md:grid <md:cols-$cols <md:auto-rows-fr'
      : '<md:flex'

  const mdClass = targets.length === 4 && alignment !== 'center'
    ? '~md:grid ~md:cols-2 ~md:auto-rows-fr ~md:items-stretch'
    : getValueForBreakpoint('md', equalTargetSize)
      ? '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch'
      : '~md:flex'

  const lgClass = targets.length === 4 && alignment !== 'center' && !inlineCTAs
    ? 'lg:grid lg:cols-2 lg:auto-rows-fr lg:items-stretch'
    : getValueForBreakpoint('lg', equalTargetSize)
      ? 'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch'
      : 'lg:flex'

  return ['gap-2 wrap', smClass, mdClass, lgClass]
})

const textAlignmentClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
}

const ctaAlignmentClasses = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end'
}

const textContainerClasses = computed(() => ({
  'md:max-w-full': alignment === 'center',
  'md:max-w-2xl': alignment !== 'center'
}))

const titleTag = computed(() => titleH1
  ? 'h1'
  : isPartOfCollection
    ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
    : firstElement ? 'h1' : 'h2')

const roundedCornersClasses = getRoundedCorners(content, componentCorners)
</script>
